import { useEffect, useState } from 'react';
import Hero from './components/Hero';
import KDRanking from './components/KDRanking';
import FlashEffectivenessPanel from './components/FlashEffectivenessPanel';
import DamageHitgroupPanel from './components/DamageHitgroupPanel';
import RoundStatsPanel from './components/RoundStatsPanel';
import Topbar from './components/Topbar';
import { useAnalysis } from './hooks/useAnalysis';

type Mode = 'example' | 'upload';

function App() {
  const [mode, setMode] = useState<Mode>('example');
  const { uploadState, data, error, selectedFileName, analyzeFile, analyzeFromUrl, reset } =
    useAnalysis();

  const isExample = mode === 'example';
  const isUploading = uploadState === 'uploading';

  const statusLabel = isExample
    ? uploadState === 'uploading'
      ? 'Loading example match'
      : uploadState === 'success'
        ? 'Example match loaded'
        : uploadState === 'error'
          ? 'Example match failed'
          : 'Preparing example match'
    : uploadState === 'success'
      ? 'Analysis ready'
      : uploadState === 'uploading'
        ? 'Analyzing match'
        : uploadState === 'error'
          ? 'Upload failed'
          : 'Waiting for upload';
  const statusTone = isExample
    ? uploadState === 'uploading'
      ? 'border-ct/50 bg-ct/20 text-ct'
      : uploadState === 'error'
        ? 'border-t/50 bg-t/20 text-t'
        : 'border-accent/40 bg-accent/20 text-accent'
    : uploadState === 'success'
      ? 'border-accent/40 bg-accent/20 text-accent'
      : uploadState === 'uploading'
        ? 'border-ct/50 bg-ct/20 text-ct'
        : uploadState === 'error'
          ? 'border-t/50 bg-t/20 text-t'
          : 'border-t/50 bg-t/20 text-[#ffb49c]';

  const uploadFooter =
    uploadState === 'uploading'
      ? `Analyzing ${selectedFileName || 'file'}...`
      : uploadState === 'success'
        ? `Analysis complete for ${selectedFileName || 'file'}.`
        : uploadState === 'error'
          ? 'Upload failed. Please try again.'
          : 'No file selected yet. Upload to unlock stats.';

  useEffect(() => {
    if (mode === 'example') {
      const baseUrl = process.env.PUBLIC_URL ?? '';
      void analyzeFromUrl(`${baseUrl}/example.txt`, 'example.txt');
    } else {
      reset();
    }
  }, [analyzeFromUrl, mode, reset]);

  const showPanels = mode === 'upload' || Boolean(data);

  return (
    <div className="relative isolate min-h-screen w-full bg-[radial-gradient(circle_at_top_left,#2a1526_0%,#1f0d1a_45%,#140912_100%)] font-sans text-ink antialiased before:fixed before:inset-0 before:-z-10 before:pointer-events-none before:content-[''] before:bg-[radial-gradient(circle_at_15%_18%,rgba(255,254,62,0.18),transparent_45%),radial-gradient(circle_at_85%_12%,rgba(255,254,62,0.12),transparent_50%),radial-gradient(circle_at_78%_80%,rgba(168,85,247,0.16),transparent_45%)]">
      <div className="mx-auto flex min-h-screen max-w-[1200px] flex-col gap-8 px-10 pb-20 pt-8 max-[960px]:px-6">
        <Topbar statusLabel={statusLabel} statusTone={statusTone} />

        <main className="flex flex-col gap-8">
          <Hero
            mode={mode}
            onModeChange={setMode}
            isUploading={isUploading}
            uploadError={error}
            uploadResult={data}
            uploadFooter={uploadFooter}
            onFileSelected={analyzeFile}
            selectedFileName={selectedFileName}
          />

          {showPanels && (
            <section className="grid grid-cols-12 gap-5 max-[960px]:grid-cols-1">
              <FlashEffectivenessPanel isExample={isExample} data={data} />
              <KDRanking data={data} />
              <DamageHitgroupPanel data={data} />
              <RoundStatsPanel data={data} />
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
